import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../../hooks/useToast';
import {
    selectSettings,
    addDataFeedProvider,
    updateDataFeedProvider,
    removeDataFeedProvider
} from '../../store/settingsSlice';
import {
    getAvailableDataFeedProviders,
    DATA_FEED_PROVIDERS
} from '../../services/providers';
import type { DataFeedProviderType } from '../../services/providers';
import { deleteConnection, disconnectConnection } from '../../utils/connections';
import { wsClient } from '../../services/websocket';

interface DataProviderSectionProps {
    onSaveSettings: () => void;
    initialStateRef: React.MutableRefObject<{
        dataFeedProvider: string;
        tradingProvider: string;
        dataFeedConnected: boolean;
        connectedTradingProviders: Set<string>;
    }>;
}

const DataProviderSection = ({ onSaveSettings }: DataProviderSectionProps) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { showToast } = useToast();
    const settings = useAppSelector(selectSettings);
    const dataProviders = useAppSelector(selectSettings).dataFeedProviders;

    const [isConnecting, setIsConnecting] = useState(false);
    const [editingProvider, setEditingProvider] = useState<string | null>(null);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<Record<string, string>>({});

    const [selectedProvider, setSelectedProvider] = useState<DataFeedProviderType>('' as DataFeedProviderType);

    const handleProviderChange = (providerType: DataFeedProviderType) => {
        setSelectedProvider(providerType);
        setCredentials({});
    };

    const handleCredentialChange = (key: string, value: string) => {
        setCredentials(prev => ({ ...prev, [key]: value }));
    };

    const handleConnect = async () => {

        setIsConnecting(true);
        try {
            const { ProviderFactory } = await import('../../services/providers');
            const provider = ProviderFactory.getDataFeedProvider(selectedProvider);
            const connection = await provider?.connect(credentials);

            if (!connection) {
                showToast('Failed to connect. Please check your credentials.', 'error');
                return;
            }

            dispatch(addDataFeedProvider(connection));

            showToast(`Connected to ${DATA_FEED_PROVIDERS[selectedProvider]?.name}`, 'success');

            // Reset selection and credentials for next connection
            setSelectedProvider('' as DataFeedProviderType);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[DataProvider] Connection error:', error);
            showToast(`Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsConnecting(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingProvider) return;

        const provider = dataProviders.find(p => p.id === editingProvider);
        if (!provider) return;

        setIsConnecting(true);
        try {
            // Disconnect first if connected
            if (provider.status === 'connected') {
                await disconnectConnection(provider.id);
            }

            // Reconnect with new credentials
            const { ProviderFactory } = await import('../../services/providers');
            const providerInstance = ProviderFactory.getDataFeedProvider(provider.providerName as DataFeedProviderType);
            const updatedConnection = await providerInstance?.connect(credentials);

            if (!updatedConnection) {
                showToast('Failed to update credentials. Please check your credentials.', 'error');
                return;
            }

            // Update in store with same ID
            dispatch(updateDataFeedProvider({ ...updatedConnection, id: provider.id }));

            showToast('Credentials updated successfully', 'success');
            setEditingProvider(null);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[DataProvider] Update error:', error);
            showToast(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsConnecting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingProvider(null);
        setCredentials({});
    };

    const handleDisconnect = async (connectionId: string) => {
        try {
            console.log('[DataProvider] Disconnecting connection:', connectionId);
            const provider = dataProviders.find(p => p.id === connectionId);
            console.log('[DataProvider] Found provider:', provider);
            if (!provider) return;



            await disconnectConnection(connectionId);

            // Update status in store
            dispatch(updateDataFeedProvider({
                ...provider,
                status: 'disconnected'
            }));

            showToast('Disconnected from provider', 'info');
            setShowDisconnectDialog(null);
            onSaveSettings();
        } catch (error) {
            console.error('[DataProvider] Disconnect error:', error);
            showToast(`Failed to disconnect: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            setShowDisconnectDialog(null);
        }
    };

    const handleDelete = async (connectionId: string) => {
        try {
            await deleteConnection(connectionId);

            // Remove from store
            dispatch(removeDataFeedProvider(connectionId));

            showToast('Provider connection deleted', 'info');
            setShowDeleteDialog(null);
            setEditingProvider(null);
            setCredentials({});
            onSaveSettings();
        } catch (error) {
            console.error('[DataProvider] Delete error:', error);
            showToast(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
            setShowDeleteDialog(null);
        }
    };

    const handleReconnect = async (connectionId: string) => {
        if (connectionId) {
            const provider = dataProviders.find(p => p.id === connectionId);
            console.log('[DataProvider] Reconnecting connection:', dataProviders);
            if (!provider) return;
            try {
                const { ProviderFactory } = await import('../../services/providers');
                const providerInstance = ProviderFactory.getDataFeedProvider(provider.providerName as DataFeedProviderType);
                if (!providerInstance) throw new Error('Unsupported provider for reconnection');
                providerInstance.updateCredentials(provider.credentials as any);
                await providerInstance?.reconnect(connectionId);

                showToast(`Reconnected to ${DATA_FEED_PROVIDERS[provider.providerName as DataFeedProviderType]?.name}`, 'success');
                onSaveSettings();

                wsClient.sendMessage({
                    type: 'provider_connect',
                    payload: { connectionId: provider.id }
                });

            } catch (err) {
                console.error('[DataProvider] Reconnection error:', err);
                showToast(`Failed to reconnect: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
            }

        }

    }

    const providerConfig = selectedProvider ? DATA_FEED_PROVIDERS[selectedProvider] : null;
    const requiredFields = providerConfig?.credentialFields?.filter(f => f.required) || [];
    const missingRequired = requiredFields.some(field =>
        !credentials[field.key] || credentials[field.key] === ''
    );

    return (
        <div style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            boxShadow: 'var(--shadow-sm)'
        }}>
            {/* Header */}
            <div style={{
                marginBottom: 'var(--spacing-lg)',
                paddingBottom: 'var(--spacing-lg)',
                borderBottom: '1px solid var(--color-border-light)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected') ? 'var(--spacing-md)' : 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-600)">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h2 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        margin: 0
                    }}>
                        Market Data Provider
                    </h2>
                </div>

                {/* Connected Providers Chips */}
                {dataProviders.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--spacing-sm)'
                    }}>
                        {dataProviders.map(provider => (
                            <div
                                key={provider.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-sm)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: provider.status === 'connected' ? 'var(--color-success-50)' : 'var(--color-danger-50)',
                                    border: provider.status === 'connected' ? '1px solid var(--color-success-200)' : '1px solid var(--color-danger-200)',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)'
                                }}
                            >
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: provider.status === 'connected' ? 'var(--color-success-600)' : 'var(--color-danger-600)'
                                }} />
                                <span>{DATA_FEED_PROVIDERS[provider.providerName as DataFeedProviderType]?.name}</span>
                                <div style={{
                                    display: 'flex',
                                    gap: 'var(--spacing-xs)',
                                    marginLeft: 'var(--spacing-xs)',
                                    borderLeft: provider.status === 'connected' ? '1px solid var(--color-success-200)' : '1px solid var(--color-danger-200)',
                                    paddingLeft: 'var(--spacing-sm)'
                                }}>
                                    {/* Show Connect button only for disconnected providers */}
                                    {provider.status !== 'connected' && (
                                        <button
                                            onClick={() => handleReconnect(provider.id)}
                                            disabled={isConnecting}
                                            style={{
                                                padding: 'var(--spacing-xs)',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: isConnecting ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: 'var(--color-danger-700)',
                                                transition: 'all 200ms',
                                                borderRadius: 'var(--radius-md)',
                                                opacity: isConnecting ? 0.5 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isConnecting) {
                                                    e.currentTarget.style.backgroundColor = 'var(--color-danger-100)';
                                                    e.currentTarget.style.color = 'var(--color-success-600)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = 'var(--color-danger-700)';
                                            }}
                                            title="Reconnect"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setEditingProvider(provider.id);
                                            setCredentials(provider.credentials || {});
                                        }}
                                        style={{
                                            padding: 'var(--spacing-xs)',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)',
                                            transition: 'all 200ms',
                                            borderRadius: 'var(--radius-md)'
                                        }}
                                        onMouseEnter={(e) => {
                                            const bgColor = provider.status === 'connected' ? 'var(--color-success-100)' : 'var(--color-danger-100)';
                                            e.currentTarget.style.backgroundColor = bgColor;
                                            e.currentTarget.style.color = 'var(--color-primary-600)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)';
                                        }}
                                        title="Edit credentials"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    {/* Show Disconnect button only for connected providers */}
                                    {provider.status === 'connected' && (
                                        <button
                                            onClick={() => setShowDisconnectDialog(provider.id)}
                                            style={{
                                                padding: 'var(--spacing-xs)',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: 'var(--color-success-700)',
                                                transition: 'all 200ms',
                                                borderRadius: 'var(--radius-md)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-warning-50)';
                                                e.currentTarget.style.color = 'var(--color-warning-700)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = 'var(--color-success-700)';
                                            }}
                                            title="Disconnect (keep connection)"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                            </svg>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowDeleteDialog(provider.id)}
                                        style={{
                                            padding: 'var(--spacing-xs)',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)',
                                            transition: 'all 200ms',
                                            borderRadius: 'var(--radius-md)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-danger-50)';
                                            e.currentTarget.style.color = 'var(--color-danger-700)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = provider.status === 'connected' ? 'var(--color-success-700)' : 'var(--color-danger-700)';
                                        }}
                                        title="Delete connection permanently"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {/* Provider Selection */}
                {!editingProvider && (
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: 'var(--spacing-xs)',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: 'var(--color-text-secondary)'
                        }}>
                            Provider
                        </label>
                        <select
                            value={selectedProvider}
                            onChange={(e) => handleProviderChange(e.target.value as DataFeedProviderType)}
                            style={{
                                width: '100%',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border-light)',
                                backgroundColor: 'var(--color-surface)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                transition: 'border-color 200ms ease-in-out'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = 'var(--color-primary-600)'}
                            onBlur={(e) => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                        >
                            <option value="">Select a provider...</option>
                            {getAvailableDataFeedProviders(settings.country).map(provider => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.name}
                                </option>
                            ))}
                        </select>
                        {selectedProvider && providerConfig && (
                            <p style={{
                                fontSize: '0.75rem',
                                color: 'var(--color-text-secondary)',
                                margin: 'var(--spacing-xs) 0 0 0'
                            }}>
                                {providerConfig.description}
                            </p>
                        )}
                    </div>
                )}

                {/* Editing existing provider */}
                {editingProvider && (
                    <div style={{
                        padding: 'var(--spacing-sm)',
                        backgroundColor: 'var(--color-primary-50)',
                        border: '1px solid var(--color-primary-200)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        <p style={{
                            fontSize: '0.875rem',
                            color: 'var(--color-primary-700)',
                            margin: 0,
                            fontWeight: '500'
                        }}>
                            Editing: {DATA_FEED_PROVIDERS[dataProviders.find(provider => provider.id === editingProvider)?.providerName as DataFeedProviderType]?.name}
                        </p>
                    </div>
                )}

                {/* Credential Fields */}
                {(selectedProvider || editingProvider) && (() => {
                    // Get the provider name - either from selectedProvider or from the editing provider connection
                    const providerName = editingProvider
                        ? dataProviders.find(p => p.id === editingProvider)?.providerName as DataFeedProviderType
                        : selectedProvider;

                    return DATA_FEED_PROVIDERS[providerName]?.credentialFields?.map((field) => {
                        const fieldValue = field.valueGenerator
                            ? field.valueGenerator(user?.userId)
                            : (field.value !== undefined ? field.value : credentials[field.key] || '');

                        const handleCopy = async () => {
                            try {
                                await navigator.clipboard.writeText(fieldValue);
                                showToast('Copied to clipboard', 'success');
                            } catch (err) {
                                showToast('Failed to copy', 'error');
                            }
                        };

                        return (
                            <div key={field.key}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: 'var(--spacing-xs)',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {field.label} {field.required && <span style={{ color: 'var(--color-danger-600)' }}>*</span>}
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={field.type}
                                        value={fieldValue}
                                        onChange={(e) => !field.readonly && handleCredentialChange(field.key, e.target.value)}
                                        disabled={field.readonly}
                                        readOnly={field.readonly}
                                        placeholder={field.placeholder}
                                        style={{
                                            width: '100%',
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            paddingRight: field.copyable ? 'calc(var(--spacing-md) + 32px)' : 'var(--spacing-md)',
                                            borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--color-border-light)',
                                            backgroundColor: field.readonly ? 'var(--color-gray-50)' : 'var(--color-surface)',
                                            fontSize: '0.875rem',
                                            color: 'var(--color-text-primary)',
                                            cursor: field.readonly ? 'default' : 'text',
                                            transition: 'border-color 200ms ease-in-out'
                                        }}
                                        onFocus={(e) => !field.readonly && (e.currentTarget.style.borderColor = 'var(--color-primary-600)')}
                                        onBlur={(e) => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
                                    />
                                    {field.copyable && fieldValue && (
                                        <button
                                            type="button"
                                            onClick={handleCopy}
                                            style={{
                                                position: 'absolute',
                                                right: 'var(--spacing-sm)',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                padding: 'var(--spacing-xs)',
                                                backgroundColor: 'var(--color-surface)',
                                                border: '1px solid var(--color-border-light)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'var(--color-text-secondary)',
                                                borderRadius: 'var(--radius-md)',
                                                transition: 'all 200ms ease-in-out',
                                                boxShadow: 'var(--shadow-xs)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-primary-50)';
                                                e.currentTarget.style.borderColor = 'var(--color-primary-300)';
                                                e.currentTarget.style.color = 'var(--color-primary-600)';
                                                e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                                e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                                e.currentTarget.style.color = 'var(--color-text-secondary)';
                                                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                                            }}
                                            title="Copy to clipboard"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    });
                })()}

                {/* API credentials link */}
                {(() => {
                    const providerName = editingProvider
                        ? dataProviders.find(p => p.id === editingProvider)?.providerName as DataFeedProviderType
                        : selectedProvider;
                    const config = providerName ? DATA_FEED_PROVIDERS[providerName] : null;

                    return (selectedProvider || editingProvider) && config?.website && config?.credentialFields?.length > 0 && (
                        <a
                            href={config.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                                fontSize: '0.75rem',
                                color: 'var(--color-primary-600)',
                                textDecoration: 'none'
                            }}
                        >
                            Get API credentials from {config.name}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    );
                })()}

                {/* Action Buttons */}
                {editingProvider ? (
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button
                            onClick={handleSaveEdit}
                            style={{
                                flex: 1,
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                backgroundColor: 'var(--color-primary-600)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: 'white',
                                transition: 'all 200ms ease-in-out'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            Save Changes
                        </button>
                        <button
                            onClick={handleCancelEdit}
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-light)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-primary)',
                                transition: 'all 200ms ease-in-out'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
                        >
                            Cancel
                        </button>
                    </div>
                ) : selectedProvider && (
                    <button
                        onClick={() => handleConnect()}
                        disabled={isConnecting || missingRequired || dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected')}
                        style={{
                            width: '100%',
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            backgroundColor: 'var(--color-primary-600)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: (isConnecting || missingRequired || dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected')) ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: 'white',
                            transition: 'all 200ms ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--spacing-xs)',
                            opacity: (isConnecting || missingRequired || dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected')) ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!isConnecting && !missingRequired && !dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected')) {
                                e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {isConnecting ? (
                            <>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid white',
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 0.6s linear infinite'
                                }} />
                                Connecting...
                            </>
                        ) : dataProviders.some(p => p.providerName === selectedProvider && p.status === 'connected') ? (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Already Connected
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Connect
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Disconnect Confirmation Dialog */}
            {showDisconnectDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-lg)',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: 'var(--shadow-lg)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--color-warning-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning-600)" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                                Disconnect Provider
                            </h3>
                        </div>
                        <p style={{ margin: '0 0 var(--spacing-lg) 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                            Disconnect from <strong>{DATA_FEED_PROVIDERS[dataProviders.find(p => p.id === showDisconnectDialog)?.providerName as DataFeedProviderType]?.name}</strong>?
                            The connection will be preserved and you can reconnect anytime.
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDisconnectDialog(null)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDisconnect(showDisconnectDialog)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-warning-600)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    color: 'white'
                                }}
                            >
                                Disconnect
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-lg)',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: 'var(--shadow-lg)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--color-danger-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger-600)" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                                Delete Connection
                            </h3>
                        </div>
                        <p style={{ margin: '0 0 var(--spacing-lg) 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                            Permanently delete the connection to <strong>{DATA_FEED_PROVIDERS[dataProviders.find(p => p.id === showDeleteDialog)?.providerName as DataFeedProviderType]?.name}</strong>?
                            You'll need to re-enter your credentials to connect again.
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDeleteDialog(null)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteDialog)}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    backgroundColor: 'var(--color-danger-600)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    color: 'white'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataProviderSection;
